import { without } from 'lodash-es';
import { getGlobalSettings } from './global_settings';

const globalSettings = getGlobalSettings();
const permissions = globalSettings?.permissions || { panelVersion: 'stable' }; // TODO: change to v2 when needed

export const ADVANCED_METRICS = {
  mau_penetration: 'Monthly active user penetration',
  ...permissions.panelVersion === 'stable' ? { dau_penetration: 'Daily active user penetration' } : { wau_penetration: 'Weekly active user penetration' },
  sessions_penetration: 'Sessions penetration',
  time_spent_penetration: 'Time spent penetration',
  time_spent_segment_penetration: 'Time Spent Segment Penetration',
  average_sessions_per_dau: 'Average sessions per DAU',
  average_time_spent_per_dau: 'Average time spent per DAU',
  churn: 'User churn',
  ending_mau_app_segment_penetration: 'Cross-app overlap',
  devices_app_penetration: 'Cross-app DAU penetration',
  ending_mau_app_penetration: 'Cross-app MAU penetration',
  sessions_app_penetration: 'Cross-app Sessions penetration',
  tts_app_penetration: 'Cross-app Time Spent penetration',
  cross_app_time_spent_per_dau: 'Cross-app average time spent per DAU',
  cross_app_sessions_per_dau: 'Cross-app average sessions per DAU',
  // HELIOS METRICS
  avg_sessions_per_device: 'Average sessions per DAU',
  avg_tts_minutes_per_device: 'Avg Time Spent per DAU',
  devices_penetration: 'DAU Penetration',
  tts_penetration: 'Time Spent Penetration',
};

export const PANEL_VERSIONS = {
  V1: 'stable-public',
  'V1-private': 'stable',
  V2: 'mfour-v2-r3-0-public',
  'V2-private': 'mfour-v2-r3-0',
};

export const ADVANCED_METRICS_V2 = {
  dau_penetration: 'Daily active user penetration',
  wau_penetration: 'Weekly active user penetration',
  mau_penetration: 'Monthly active user penetration',
  sessions_penetration: 'Sessions penetration',
  time_spent_penetration: 'Time spent penetration',
  time_spent_segment_penetration: 'Time Spent Segment Penetration',
  average_sessions_per_dau: 'Average sessions per DAU',
  average_time_spent_per_dau: 'Average time spent per DAU',
  churn: 'User churn',

  cross_app_overlap: 'Cross-app overlap',
  cross_app_dau_penetration: 'Cross-app DAU penetration',
  cross_app_wau_penetration: 'Cross-app WAU penetration',
  cross_app_sessions_penetration: 'Cross-app Sessions penetration',
  cross_app_time_spent_penetration: 'Cross-app Time Spent penetration',
  cross_app_time_spent_per_dau: 'Cross-app average time spent per DAU',
  cross_app_sessions_per_dau: 'Cross-app average sessions per DAU',
};

export const ADVANCED_METRICS_V2_MAP = {
  [PANEL_VERSIONS.V1]: without(Object.keys(ADVANCED_METRICS_V2), 'wau_penetration', 'cross_app_wau_penetration'),
  [PANEL_VERSIONS['V1-private']]: without(Object.keys(ADVANCED_METRICS_V2), 'wau_penetration', 'cross_app_wau_penetration'),
  [PANEL_VERSIONS.V2]: without(Object.keys(ADVANCED_METRICS_V2), 'dau_penetration', 'cross_app_wau_penetration'),
  [PANEL_VERSIONS['V2-private']]: without(Object.keys(ADVANCED_METRICS_V2), 'dau_penetration'),
};

export const ADVANCED_METRICS_APP = {
  mau_penetration: 'MAU Penetration',
  ...permissions.panelVersion === 'stable' ? { dau_penetration: 'DAU Penetration' } : { wau_penetration: 'WAU Penetration' },
  sessions_penetration: 'Sessions Penetration',
  time_spent_penetration: 'Time Spent Penetration',
  time_spent_segment_penetration: 'Time Spent Segment Penetration',
  average_time_spent_per_dau: 'Avg Time Spent per DAU',
  average_sessions_per_dau: 'Avg Sessions per DAU',
  churn: 'Churn',
};

export const ADVANCED_METRICS_APP_DESCRIPTION = {
  mau_penetration: ' For each date, the number of devices that opened the app at least once over the last 30 days (rolling) divided by the total number of devices that had at least one app session (on any app) over the last 30 days (rolling)',
  ...permissions.panelVersion === 'stable' ? { dau_penetration: ' For each date, the number of devices that opened the app at least once that day divided by the total number of devices that had at least one app session (on any app) that day.' } : { wau_penetration: 'For each date, the number of devices that opened the app at least once in the most recent 7 days divided by the total number of devices that had at least one app session (on any app) during those days' },
  sessions_penetration: 'For each date, the total number of sessions which occurred for that app on the most recent 7 days divided by the total number of sessions for all apps over the most recent 7 days across the panel or data set',
  time_spent_penetration: 'For each date, the total number of seconds spent inside that app on the most recent 7 days divided by the total number of seconds spent inside all apps on the most recent 7 days across the panel or data set',
  time_spent_segment_penetration: 'The total time spent which occurred for the app by [Segment] users that day divided by the total total time spent by [Segment] users for all apps that day across the panel or data set.',
  average_time_spent_per_dau: 'This is an average time spent per day, per app, per device.  This ignores sessions and measures the cumulative time spent across a 24 hour period per unique device.  Unlike other metrics this is the actual time spent in minutes and seconds and not a %. It is an average over the most recent 7 days',
  average_sessions_per_dau: 'This is an average sessions per day, per app, per device.  This measures the cumulative number of sessions spent across a 24 hour period per unique device.  Unlike other metrics this is the actual number of sessions and not a %. It is an average over the most recent 7 days',
  churn: 'The % of MAUs who were active between 30 and 60 days ago on an app but were not active on the app in the most recent 30 days.',
};

export const ADVANCED_METRICS_PERCENTAGE = [
  'mau_penetration',
  ...permissions.panelVersion === 'stable' ? ['dau_penetration'] : ['wau_penetration'],
  'churn',
  'sessions_penetration',
  'time_spent_penetration',
  'time_spent_segment_penetration',
];

export const COHORTS = {
  all: 'All Users',
  all_users: 'All Users',
  male_users: 'Male Users',
  female_users: 'Female Users',
  power_users: 'Power Users',
  casual_users: 'Casual Users',
  older_users: 'Older Users',
  younger_users: 'Younger Users',
  'age_17-25': 'Age 17-25',
  'age_26-35': 'Age 26-35',
  'age_36-45': 'Age 36-45',
  'age_46+': 'Age 46+',
};

export const V2_COHORTS = {
  all_users: 'All Users',
  male_users: 'Male Users',
  female_users: 'Female Users',
  power_users: 'Power Users',
  older_users: 'Older Users',
  younger_users: 'Younger Users',
  'age_17-25': 'Age 17-25',
  'age_26-35': 'Age 26-35',
  'age_36-45': 'Age 36-45',
  'age_46+': 'Age 46+',
};

export const V2_COHORTS_MAP = {
  [PANEL_VERSIONS.V1]: without(Object.keys(V2_COHORTS), 'age_17-25', 'age_26-35', 'age_36-45', 'age_46+'),
  [PANEL_VERSIONS['V1-private']]: without(Object.keys(V2_COHORTS), 'age_17-25', 'age_26-35', 'age_36-45', 'age_46+'),
  [PANEL_VERSIONS.V2]: without(Object.keys(V2_COHORTS), 'younger_users'),
  [PANEL_VERSIONS['V2-private']]: without(Object.keys(V2_COHORTS), 'younger_users'),
};
